import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "./UserContext";
import { toast } from "react-toastify";

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const { user, userData } = useUser();

  // ADDED: Get initial state from sessionStorage
  const getInitialSelectedUserId = () => {
    if (typeof window !== "undefined") {
      const stored = sessionStorage.getItem("adminSelectedUserId");
      return stored || "default";
    }
    return "default";
  };

  // const [userAckCount, setUserAckCount] = useState(null);
  // const [userTransCount, setUserTransCount] = useState(null);
  const [adminData, setAdminData] = useState({
    userInvoices: [],
    userTemplates: [],
    userClients: [],
    userClientData: {},
  });
  const [apLoading, setLoading] = useState(false);

  // Centralized selected user state with persistence
  const [selectedUserId, setSelectedUserId] = useState(
    getInitialSelectedUserId
  );
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [isViewingOtherUser, setIsViewingOtherUser] = useState(false);

  // Helper function to check invoice system permissions
  const checkInvoiceSystemPermissions = (userClientData) => {
    if (!userClientData?.permissions?.invoiceSystem) {
      return {
        hasAccess: false,
        firstAccessibleFeature: null,
        message:
          "User has no particular access to any invoice system features.",
      };
    }

    const invoiceSystem = userClientData.permissions.invoiceSystem;

    // Define the order of features to check (you can adjust this order as needed)
    const featureOrder = [
      // Receipts
      { category: "receipts", key: "invoicesSubAccess", name: "Invoices" },
      {
        category: "receipts",
        key: "noticeChargesAccess",
        name: "Notice of Charges",
      },
      {
        category: "receipts",
        key: "ackReceiptsAccess",
        name: "Acknowledgment Receipts",
      },
      // Records - internal
      {
        category: "records.internal",
        key: "executiveAdminAccess",
        name: "Executive & Administrative",
      },
      {
        category: "records.internal",
        key: "caseBriefAccess",
        name: "Case Brief",
      },
      {
        category: "records.internal",
        key: "processServerLogsAccess",
        name: "Process Server & Messenger Logs",
      },
      {
        category: "records.internal",
        key: "secretarialLogsAccess",
        name: "Secretarial Records",
      },
      // Records - external
      {
        category: "records.external",
        key: "secretariatResolutionsAccess",
        name: "Secretariat Resolutions",
      },
      {
        category: "records.external",
        key: "transmittalAccess",
        name: "Transmittal",
      },
      {
        category: "records.external",
        key: "legalAdviceAccess",
        name: "Legal Advice",
      },
    ];

    // Check each feature in order
    for (const feature of featureOrder) {
      const [mainCategory, subCategory] = feature.category.split(".");
      let hasPermission = false;

      if (subCategory) {
        hasPermission =
          invoiceSystem[mainCategory]?.[subCategory]?.[feature.key] === true;
      } else {
        hasPermission = invoiceSystem[mainCategory]?.[feature.key] === true;
      }

      if (hasPermission) {
        return {
          hasAccess: true,
          firstAccessibleFeature: {
            key: feature.key,
            name: feature.name,
            category: feature.category,
            fullPath: subCategory
              ? `userClientData.permissions.invoiceSystem.${feature.category}.${feature.key}`
              : `userClientData.permissions.invoiceSystem.${feature.category}.${feature.key}`,
          },
          message: null,
        };
      }
    }

    // If we get here, no permissions were found to be true
    return {
      hasAccess: false,
      firstAccessibleFeature: null,
      message: "User has no particular access to any invoice system features.",
    };
  };

  // Fetch invoices for a specific user

  const fetchUserInvoices = useCallback(async (targetUserId) => {
    setLoading(true);
    try {
      // STEP 1: Check permissions first by fetching only user document
      const userDocRef = doc(db, "users", targetUserId);
      const userDocSnap = await getDoc(userDocRef);

      const clientData = userDocSnap.exists()
        ? { id: userDocSnap.id, ...userDocSnap.data() }
        : null;

      // Check invoice system permissions before fetching other data
      const permissionCheck = checkInvoiceSystemPermissions(clientData);

      if (!permissionCheck.hasAccess) {
        toast.error(permissionCheck.message);
        console.warn("Permission denied:", permissionCheck.message);

        // Return early without fetching invoices, templates, or clients
        setAdminData({
          userInvoices: [],
          userTemplates: [],
          userClients: [],
          userClientData: {},
        });

        return {
          invoices: [],
          templates: [],
          clients: [],
          clientData: null,
          permissionDenied: true,
          message: permissionCheck.message,
        };
      }

      const invoiceQ = query(
        collection(db, "invoices"),
        where("createdBy", "==", targetUserId)
      );
      const templateQ = query(
        collection(db, "invoiceTemplates"),
        where("createdBy", "==", targetUserId)
      );
      const clientsQ = query(
        collection(db, "users", targetUserId, "invoiceClients")
      );

      const [invSnap, tplSnap, clSnap] = await Promise.all([
        getDocs(invoiceQ),
        getDocs(templateQ),
        getDocs(clientsQ),
      ]);

      const invoices = invSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const templates = tplSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const clients = clSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setAdminData({
        userInvoices: invoices,
        userTemplates: templates,
        userClients: clients,
        userClientData: clientData,
      });

      return {
        invoices,
        templates,
        clients,
        clientData,
        permissionGranted: true,
        firstAccessibleFeature: permissionCheck.firstAccessibleFeature,
      };
    } catch (err) {
      console.error("Error fetching user invoices:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Centralized function to handle user selection
  const handleUserSelection = useCallback(
    async (userId) => {
      setSelectedUserId(userId);

      if (userId === "default") {
        setAdminData({
          userInvoices: [],
          userTemplates: [],
          userClients: [],
          userClientData: {},
        });
        return { success: true };
      } else {
        try {
          const result = await fetchUserInvoices(userId);
          if (result && result.permissionDenied) {
            // Reset to default if permission denied
            setSelectedUserId("default");
            setAdminData({
              userInvoices: [],
              userTemplates: [],
              userClients: [],
              userClientData: {},
            });
            return {
              success: false,
              permissionDenied: true,
              message: result.message,
            };
          }
          return {
            success: result !== null,
            data: result,
            firstAccessibleFeature: result?.firstAccessibleFeature,
          };
        } catch (error) {
          console.error("Error selecting user:", error);
          setSelectedUserId("default");
          return { success: false, error };
        }
      }
    },
    [fetchUserInvoices]
  );

  // ADDED: Persist selectedUserId to sessionStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("adminSelectedUserId", selectedUserId);
    }
  }, [selectedUserId]);

  // ADDED: Initialize admin state on mount
  useEffect(() => {
    const initializeAdminState = async () => {
      const storedUserId = getInitialSelectedUserId();
      if (storedUserId !== "default" && userData?.addedUsers?.length > 0) {
        // Always refresh if there's a stored user ID
        // console.log("Initializing admin state for:", storedUserId);
        await handleUserSelection(storedUserId);
      }
    };

    if (user) {
      initializeAdminState();
    }
  }, [user, userData?.addedUsers, handleUserSelection]);

  // Update viewing state when selectedUserId changes
  useEffect(() => {
    const isViewingOther =
      selectedUserId !== "default" && selectedUserId !== user?.uid;

    setIsViewingOtherUser(isViewingOther);

    if (isViewingOther) {
      const userInfo = userData?.addedUsers?.find(
        (u) => u.uid === selectedUserId
      );
      setSelectedUserData(userInfo || null);
    } else {
      setSelectedUserData(null);
    }
  }, [selectedUserId, user?.uid, userData?.addedUsers]);

  // Reset to default when user logs out or changes
  useEffect(() => {
    if (!user) {
      setSelectedUserId("default");
      setSelectedUserData(null);
      setIsViewingOtherUser(false);
      setAdminData({
        userInvoices: [],
        userTemplates: [],
        userClients: [],
        userClientData: {},
      });
      // Clear sessionStorage
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("adminSelectedUserId");
      }
    }
  }, [user]);

  // Get the display name for the currently selected user
  const getSelectedUserName = () => {
    if (selectedUserId === "default") {
      return "My Data";
    }
    return selectedUserData?.name || "Unknown User";
  };

  // Get available users for dropdown
  const getAvailableUsers = () => {
    if (!userData?.addedUsers) return [];
    return userData.addedUsers;
  };

  // Check if user can perform write operations
  const canEdit = useCallback(() => {
    if (!isViewingOtherUser) return true;
    return false;
  }, [isViewingOtherUser]);

  // Show appropriate warning/info messages
  const showViewingInfo = () => {
    if (isViewingOtherUser) {
      const mode = "Admin mode";
      toast.info(`Managing ${getSelectedUserName()}'s data - ${mode}`, {
        toastId: "viewing-other-user",
        autoClose: 3000,
      });
    }
  };

  // Helper function to add a new client to the state
  const addUserInvoiceClient = useCallback((newClient) => {
    setAdminData((prev) => {
      const updatedClients = [...prev.userClients, newClient];
      return {
        ...prev,
        userClients: updatedClients.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        ),
      };
    });
  }, []);

  // Helper function to update a client in the state
  const updateUserInvoiceClient = useCallback((clientId, updatedData) => {
    setAdminData((prev) => {
      const updatedClients = prev.userClients
        .map((client) =>
          client.id === clientId ? { ...client, ...updatedData } : client
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      return {
        ...prev,
        userClients: updatedClients,
      };
    });
  }, []);

  const deleteUserInvoiceClient = useCallback((clientId) => {
    setAdminData((prev) => {
      const updatedClients = prev.userClients.filter(
        (client) => client.id !== clientId
      );

      return {
        ...prev,
        userClients: updatedClients,
      };
    });
  }, []);

  // Helper function to add a new invoice to the state
  const addUserInvoice = useCallback((newInvoice) => {
    setAdminData((prev) => ({
      ...prev,
      userInvoices: [...prev.userInvoices, newInvoice],
    }));
  }, []);

  // Helper function to update an invoice in the state
  const updateUserInvoice = useCallback((invoiceId, updatedData) => {
    setAdminData((prev) => ({
      ...prev,
      userInvoices: prev.userInvoices.map((invoice) =>
        invoice.id === invoiceId ? { ...invoice, ...updatedData } : invoice
      ),
    }));
  }, []);

  // Helper function to delete an invoice from the state
  const deleteUserInvoice = useCallback((invoiceId) => {
    setAdminData((prev) => ({
      ...prev,
      userInvoices: prev.userInvoices.filter(
        (invoice) => invoice.id !== invoiceId
      ),
    }));
  }, []);

  // Helper function to delete a template from the state
  const deleteUserTemplate = useCallback((templateId) => {
    setAdminData((prev) => ({
      ...prev,
      userTemplates: prev.userTemplates.filter(
        (template) => template.id !== templateId
      ),
    }));
  }, []);

  // Helper function to add a new template to the state
  const addUserTemplate = useCallback((newTemplate) => {
    setAdminData((prev) => ({
      ...prev,
      userTemplates: [...prev.userTemplates, newTemplate],
    }));
  }, []);

  // Helper function to update a template in the state
  const updateUserTemplate = useCallback((templateId, updatedData) => {
    setAdminData((prev) => ({
      ...prev,
      userTemplates: prev.userTemplates.map((template) =>
        template.id === templateId ? { ...template, ...updatedData } : template
      ),
    }));
  }, []);

  const contextValue = {
    // Data
    adminData,
    setAdminData,
    apLoading,

    // Acknowledgement and Transmittal counters
    // userAckCount,
    // userTransCount,
    // setUserAckCount,
    // setUserTransCount,

    // User selection state
    selectedUserId,
    selectedUserData,
    isViewingOtherUser,

    // Functions
    fetchUserInvoices,
    handleUserSelection,
    getSelectedUserName,
    getAvailableUsers,
    checkInvoiceSystemPermissions, // Expose this function for external use

    addUserInvoiceClient,
    updateUserInvoiceClient,
    deleteUserInvoiceClient,
    addUserInvoice,
    updateUserInvoice,
    deleteUserInvoice,
    addUserTemplate,
    updateUserTemplate,
    deleteUserTemplate,

    // Permissions
    canEdit,

    // Utilities
    showViewingInfo,
  };

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdminContext = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdminContext must be used within AdminProvider");
  }
  return context;
};
